import Img from '../components/Img';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import { Helmet } from '@dr.pogodin/react-helmet';

const SMS_NUMBER = '5082031342';
const DISPLAY_NUMBER = '508-203-1342';

const REVIEWS = [
  {
    name: 'Stephanie M.',
    text: 'Quick and minimal pain. Convenient location and great customer service. Owner was honest and reassuring every step of the way.',
  },
  {
    name: 'Kimberly L.',
    text: "What a great experience! Go get your consult now and put your mind at ease. Super easy, clean, professional and almost pain free. Already have my next session booked and couldn't be happier.",
  },
  {
    name: 'Gordon S.',
    text: 'Craig does an excellent job all around, and is the most reasonable in pricing for tattoo removal in the area…',
  },
];

const trackClick = (event: string, location: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, button_location: location });
};

const TattooRemoval: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', textAlign: 'justify' }}>
      <Helmet>
        <title>Tattoo Removal in Wrentham, MA | Atomic Tattoo Removal</title>
        <meta
          name="description"
          content="Safe, effective, and professional laser tattoo removal in Wrentham, MA. Reveal your skin’s true canvas with Atomic Tattoo Removal. Free consultations available."
        />
        <meta
          name="keywords"
          content="tattoo removal, laser tattoo removal, Wrentham MA, professional tattoo removal, tattoo fading, cover-up tattoo removal, safe tattoo removal"
        />
        <meta property="og:title" content="Tattoo Removal in Wrentham, MA | Atomic Tattoo Removal" />
        <meta
          property="og:description"
          content="Reveal your skin’s true canvas with safe and effective laser tattoo removal in Wrentham, MA. Book your free consultation with Atomic Tattoo Removal today."
        />
        <meta property="og:image" content="https://www.atomictattooremoval.com/assets/tattoo-removal.png" />
        <meta property="og:url" content="https://www.atomictattooremoval.com/tattoo-removal" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Laser Tattoo Removal",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Atomic Tattoo Removal",
              "image": "https://www.atomictattooremoval.com/assets/atomic-tattoo-removal-logo.png",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Wrentham",
                "addressRegion": "MA",
                "postalCode": "02093",
                "addressCountry": "US"
              },
              "url": "https://www.atomictattooremoval.com"
            },
            "areaServed": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Wrentham",
                "addressRegion": "MA"
              }
            },
            "description": "Safe, effective, and professional laser tattoo removal services in Wrentham, MA. Reveal your skin’s true canvas with Atomic Tattoo Removal.",
            "url": "https://www.atomictattooremoval.com/tattoo-removal"
          })}
        </script>
      </Helmet>
      <h1 style={{ color: '#cb4f2e', textAlign: 'center', marginBottom: '20px' }}>
        Tattoo Removal in Wrentham, MA
      </h1>
      <h2 style={{ color: '#5C96AC', textAlign: 'center', marginBottom: '20px' }}>
        Reveal Your Skin’s True Canvas
      </h2>
      <p>
        Whether you want to completely remove an unwanted tattoo or lighten it for a cover-up, Atomic Tattoo Removal provides safe, effective, and personalized laser tattoo removal services in Wrentham, MA. Our expert team uses advanced laser technology to help you achieve your desired results with minimal discomfort and maximum care.
      </p>

      {/* ── QUICK START: PRICE + TEXT / CALL ─────────────────── */}
      <div
        style={{
          textAlign: 'center',
          background: '#f7f3ea',
          border: '1px solid #e5dccb',
          borderRadius: '10px',
          padding: '20px',
          margin: '24px 0',
        }}
      >
        <p style={{ margin: '0 0 6px', fontSize: '1.3rem', fontWeight: 700, color: '#cb4f2e' }}>
          Sessions from $80
        </p>
        <p style={{ margin: '0 0 16px' }}>
          Extra-small tattoos from $80 per session, small tattoos from $150.{' '}
          <Link to="/pricing" style={{ color: '#cb4f2e', textDecoration: 'underline' }}>
            See full pricing
          </Link>
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {isMobile && (
            <Button
              component="a"
              href={`sms:${SMS_NUMBER}`}
              color="secondary"
              variant="contained"
              onClick={() => trackClick('sms_click', 'tattoo_removal_quick_start')}
              sx={{ color: '#f0e9d4', '&:hover': { color: '#ffffff', backgroundColor: '#cb4f2e' } }}
            >
              📱 Text Us a Photo
            </Button>
          )}
          <Button
            component="a"
            href={`tel:${SMS_NUMBER}`}
            color="secondary"
            variant={isMobile ? 'outlined' : 'contained'}
            onClick={() => trackClick('call_click', 'tattoo_removal_quick_start')}
            sx={isMobile ? undefined : { color: '#f0e9d4', '&:hover': { color: '#ffffff', backgroundColor: '#cb4f2e' } }}
          >
            📞 Call {DISPLAY_NUMBER}
          </Button>
        </div>
        <p style={{ margin: '14px 0 0', fontSize: '0.95rem' }}>
          {isMobile
            ? 'Text us a photo of your tattoo for a price estimate.'
            : `Text a photo of your tattoo to ${DISPLAY_NUMBER} for a price estimate.`}{' '}
          Owner-operated, not a franchise. Texts are answered directly by owner Craig Howard.
        </p>
      </div>

      <div style={{ textAlign: 'center', margin: '30px 0' }}>
        <Img
          src="/assets/tattoo-removal.png"
          alt="Laser tattoo removal procedure"
          style={{ maxWidth: '100%', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        />
        <div style={{ marginTop: '16px' }}>
          <Link to="/before-after-images" style={{ color: '#cb4f2e', textDecoration: 'underline', fontWeight: 500 }}>
            See our before and after gallery for more results
          </Link>
        </div>
      </div>
      <h3 style={{ color: '#cb4f2e', marginTop: '30px' }}>How Laser Tattoo Removal Works</h3>
      <p>
        Our state-of-the-art lasers emit ultra-short pulses of light energy that penetrate the skin and break down tattoo ink particles. These particles are then naturally eliminated by your body’s immune system over time. Multiple sessions are typically required, spaced several weeks apart, to achieve optimal results.
      </p>
      <h3 style={{ color: '#cb4f2e', marginTop: '30px' }}>What to Expect</h3>
      <ul style={{ paddingLeft: '20px' }}>
        <li>
          <strong>Consultation:</strong> We’ll assess your tattoo and discuss your goals to create a customized treatment plan.
        </li>
        <li>
          <strong>Treatment Sessions:</strong> Each session lasts between 10 to 30 minutes, depending on the size and complexity of your tattoo.
        </li>
        <li>
          <strong>Aftercare:</strong> We provide detailed aftercare instructions to ensure proper healing and the best possible results.
        </li>
      </ul>

      {/* ── REVIEWS ──────────────────────────────────────────── */}
      <h3 style={{ color: '#cb4f2e', marginTop: '30px' }}>What Our Clients Say</h3>
      <p style={{ marginTop: 0 }}>
        <strong>★★★★★ 4.7 on Google</strong> (15 reviews)
      </p>
      {REVIEWS.map(review => (
        <blockquote
          key={review.name}
          style={{
            margin: '0 0 14px',
            padding: '12px 16px',
            borderLeft: '4px solid #5C96AC',
            background: '#f9f9f9',
            borderRadius: '4px',
            textAlign: 'left',
          }}
        >
          <div style={{ color: '#f5a623', marginBottom: '4px' }}>★★★★★</div>
          <div>“{review.text}”</div>
          <div style={{ marginTop: '6px', fontSize: '0.9rem', color: '#555' }}>— {review.name}, Google review</div>
        </blockquote>
      ))}

      <h3 style={{ color: '#cb4f2e', marginTop: '30px' }}>Why Choose Atomic Tattoo Removal?</h3>
      <ul style={{ paddingLeft: '20px' }}>
        <li>
          <strong>Owner-Operated:</strong> Locally owned and run, not a franchise or corporate chain. You work directly with the owner from your consultation to your final session.
        </li>
        <li>
          <strong>Experienced Professionals:</strong> Our certified technicians are trained in the latest laser removal techniques.
        </li>
        <li>
          <strong>Personalized Care:</strong> Every treatment plan is tailored to your unique skin type, tattoo, and goals.
        </li>
        <li>
          <strong>Comfort & Safety:</strong> Your comfort and safety are our top priorities throughout the removal process.
        </li>
      </ul>
      <p style={{ marginTop: '20px' }}>
        Ready to start your tattoo removal journey? <strong>Contact us today</strong> to schedule your free consultation and take the first step toward clear, confident skin.
      </p>
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <Link to="/book-consultation" style={{ textDecoration: 'none' }}>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: 'book_now_click',
                button_location: 'tattoo_removal_page',
              });
            }}
            sx={{
              color: '#f0e9d4',
              '&:hover': { color: '#ffffff', backgroundColor: '#cb4f2e' },
            }}
          >
            Book Your Free Consultation
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default TattooRemoval;
